import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GoogleLogo, Envelope, Lock, User } from "@phosphor-icons/react";
import { AuthShell, Field } from "@/pages/Login";

export default function Register() {
  const { user, loading, register, beginGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen ledger-paper flex items-center justify-center">
        <p className="font-mono-ledger text-xs text-[#5C5F58] uppercase tracking-[0.25em]">Opening the vault…</p>
      </div>
    );
  }
  const next = searchParams.get("next") || "/";
  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    const res = await register(email, password, name);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else toast.success("Your ledger is open.");
  };

  return (
    <AuthShell>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Open a Standing Record
      </div>
      <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight mb-1">Create your ledger.</h1>
      <p className="text-sm text-[#5C5F58] mb-6">
        A private, permanent record of physical wealth.
      </p>

      <button
        onClick={beginGoogle}
        data-testid="google-register-btn"
        className="w-full inline-flex items-center justify-center gap-3 px-4 py-3 text-sm border border-[#0A192F] bg-white hover:bg-[#0A192F] hover:text-[#F9F8F6] transition-colors mb-6"
      >
        <GoogleLogo size={16} weight="bold" />
        Continue with Google
      </button>

      <div className="flex items-center gap-3 text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mb-6">
        <span className="h-px flex-1 bg-[#C9C4B8]" />
        or email
        <span className="h-px flex-1 bg-[#C9C4B8]" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field icon={<User size={14} />} label="Name (optional)">
          <input
            data-testid="register-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>
        <Field icon={<Envelope size={14} />} label="Email">
          <input
            data-testid="register-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>
        <Field icon={<Lock size={14} />} label="Password (min 8 chars)">
          <input
            data-testid="register-password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>
        <Field icon={<Lock size={14} />} label="Confirm password">
          <input
            data-testid="register-confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>

        {error && (
          <div className="text-xs text-[#6B1718] bg-[#6B1718]/5 border border-[#6B1718]/30 px-3 py-2" data-testid="register-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="register-submit"
          className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
        >
          {submitting ? "Opening…" : "Open my ledger"}
        </button>
      </form>

      <p className="text-xs text-[#5C5F58] mt-6 text-center">
        Already registered?{" "}
        <Link
          to={searchParams.get("next") ? `/login?next=${encodeURIComponent(searchParams.get("next"))}` : "/login"}
          className="underline underline-offset-4 text-[#1A3622]"
          data-testid="link-login"
        >
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
